import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { PillarData } from '../../lib/pillarTypes';
import ProjectTeamRoster from '../../Components/ProjectTeam/ProjectTeamRoster';
import type { ViewerTier } from '../../Components/ProjectTeam/projectTeamTypes';
import ProjectPartnersRow from '../../Components/ProjectPartners/ProjectPartnersRow';
import AddPartnerModal from '../../Components/ProjectPartners/AddPartnerModal';
import ProjectContentSections from '../../Components/ProjectContentSections/ProjectContentSections';
import { useApiCommunityData } from '../../api/useApiCommunityData';
import { useApiProjectPartners } from '../../api/useApiProjectPartners';
import { canEditProjectContent } from '../../security/securityAdapter';
import { getCurrentUserGuid } from '../../api/client';
import { setProjectOverride } from '../../Data/ProjectConfig/projectConfigOverrides';
import styles from './ProjectDetail.module.css';


/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface CoreTabPanelProps {
  data: PillarData;
  projectGuid: string;
  /** Viewer's permission tier for team member visibility filtering. */
  viewerTier: ViewerTier;
  /** Called when user clicks "See all team members" to show full team page. */
  onShowTeamList?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Description box — matches Community MVV "band" pattern             */
/* ------------------------------------------------------------------ */

const descBoxStyle: React.CSSProperties = {
  background: 'var(--bg-elevated)',
  borderRadius: 14,
  padding: '24px 28px',
  borderInlineStart: '4px solid var(--pillar-accent, #6B7280)',
  marginBlockStart: 16,
  marginBlockEnd: 24,
};

const descTextStyle: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
  margin: 0,
  whiteSpace: 'pre-wrap',
};

const descTextareaStyle: React.CSSProperties = {
  width: '100%',
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
  background: 'var(--bg-hover)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  padding: '10px 12px',
  margin: 0,
  resize: 'vertical',
  fontFamily: 'inherit',
  minHeight: 80,
};

/* ------------------------------------------------------------------ */
/*  Edit toggle button (right-aligned, subtle)                         */
/* ------------------------------------------------------------------ */

const editToggleRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginBlockEnd: 4,
};

const editToggleBtnStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  color: 'var(--text-muted)',
  fontSize: 12,
  padding: '4px 14px',
  cursor: 'pointer',
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function CoreTabPanel({
  data,
  projectGuid,
  viewerTier,
  onShowTeamList,
}: CoreTabPanelProps) {
  const { t } = useAppTranslation('pages');
  const currentUserGuid = getCurrentUserGuid();
  const canEdit = canEditProjectContent(currentUserGuid, projectGuid);

  // Page-level edit mode toggle
  const [isEditingAbout, setIsEditingAbout] = useState(false);

  // Inline description editing
  const [descDraft, setDescDraft] = useState(data.description || '');
  const descDraftRef = useRef(descDraft);
  descDraftRef.current = descDraft;

  // Sync draft when data changes from outside (e.g. settings modal update)
  useEffect(() => {
    setDescDraft(data.description || '');
  }, [data.description]);

  // Save description on blur or when leaving edit mode
  const saveDescription = useCallback(() => {
    const trimmed = descDraftRef.current.trim();
    if (trimmed !== (data.description || '').trim()) {
      setProjectOverride(projectGuid as any, { description: trimmed } as any);
    }
  }, [projectGuid, data.description]);

  // Auto-save description when edit mode is toggled off
  const prevEditingRef = useRef(false);
  useEffect(() => {
    if (prevEditingRef.current && !isEditingAbout) {
      saveDescription();
    }
    prevEditingRef.current = isEditingAbout;
  }, [isEditingAbout, saveDescription]);

  // Community entities for resolving linked partner org display data
  const { communityEntities, communityRelationships } = useApiCommunityData();

  // Partner mutations (for the add-partner modal and link-entity)
  const { addPartner, editPartner } = useApiProjectPartners(projectGuid);

  // Add Partner modal state
  const [showAddPartner, setShowAddPartner] = useState(false);
  const handleAddPartnerOpen = useCallback(() => setShowAddPartner(true), []);
  const handleAddPartnerClose = useCallback(() => setShowAddPartner(false), []);

  // Link Entity modal state — reuses AddPartnerModal in "link" mode
  const [linkPartnerId, setLinkPartnerId] = useState<string | null>(null);
  const handleLinkEntity = useCallback((partnerId: string) => {
    setLinkPartnerId(partnerId);
  }, []);
  const handleLinkEntityClose = useCallback(() => setLinkPartnerId(null), []);
  const handleLinkEntitySubmit = useCallback(async (body: { entityGuid?: string }) => {
    if (linkPartnerId && body.entityGuid) {
      await editPartner(linkPartnerId, { entityGuid: body.entityGuid });
    }
    setLinkPartnerId(null);
  }, [linkPartnerId, editPartner]);
  const entityMap = useMemo(() => {
    const m = new Map<string, Record<string, any>>();
    if (Array.isArray(communityEntities)) {
      for (const e of communityEntities) {
        const guid = e.entity_guid;
        if (guid) {
          m.set(String(guid).toUpperCase(), e);
        }
      }
    }
    return m;
  }, [communityEntities]);

  return (
    <div className={styles.panel}>

      {/* ── Edit Page toggle (top-right, editors only) ── */}
      {canEdit && (
        <div style={editToggleRowStyle}>
          <button
            style={editToggleBtnStyle}
            onClick={() => setIsEditingAbout((v) => !v)}
          >
            {isEditingAbout
              ? t('projectDetail.about.doneEditing')
              : t('projectDetail.about.editPage')}
          </button>
        </div>
      )}

      {/* ── 1. Description — bounded box with left accent highlight ── */}
      {isEditingAbout ? (
        <div style={descBoxStyle}>
          <textarea
            style={descTextareaStyle}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            onBlur={saveDescription}
            placeholder={t('projectDetail.about.descriptionPlaceholder')}
            rows={4}
          />
        </div>
      ) : data.description ? (
        <div style={descBoxStyle}>
          <p style={descTextStyle}>
            {data.description}
          </p>
        </div>
      ) : null}

      {/* ── 1b. Overview — lead narrative pinned above Team ──
          Shared overview slot (section_type='overview'); renders nothing
          until an Overview is added from the Background block below. */}
      {projectGuid && (
        <ProjectContentSections
          projectGuid={projectGuid}
          canEdit={canEdit}
          isEditing={isEditingAbout}
          slot="overview"
        />
      )}

      {/* ── 2. Project Team Roster ── */}
      {projectGuid && (
        <ProjectTeamRoster
          projectGuid={projectGuid}
          projectName={data.name}
          viewerTier={viewerTier}
          showVacant={true}
          onShowTeamList={onShowTeamList}
        />
      )}

      {/* ── 3. Partners — landscape cards ── */}
      {projectGuid && (
        <ProjectPartnersRow
          projectGuid={projectGuid}
          canEdit={canEdit}
          isEditing={isEditingAbout}
          entityMap={entityMap}
          communityEntities={Array.isArray(communityEntities) ? communityEntities : []}
          communityRelationships={Array.isArray(communityRelationships) ? communityRelationships : []}
          onAddPartner={handleAddPartnerOpen}
          onLinkEntity={handleLinkEntity}
        />
      )}

      {/* ── Add Partner Modal ── */}
      {showAddPartner && (
        <AddPartnerModal
          projectGuid={projectGuid}
          entities={Array.isArray(communityEntities) ? communityEntities : []}
          relationships={Array.isArray(communityRelationships) ? communityRelationships : []}
          // DEV-963: AddPartnerModal expects Promise<void>; addPartner resolves
          // with the created partner. Wrap to discard the return value.
          onSubmit={async (body) => { await addPartner(body); }}
          onClose={handleAddPartnerClose}
        />
      )}

      {/* ── Link Entity Modal (reuses AddPartnerModal for entity search) ── */}
      {linkPartnerId && (
        <AddPartnerModal
          projectGuid={projectGuid}
          entities={Array.isArray(communityEntities) ? communityEntities : []}
          relationships={Array.isArray(communityRelationships) ? communityRelationships : []}
          onSubmit={handleLinkEntitySubmit as any}
          onClose={handleLinkEntityClose}
          entityOnly
        />
      )}

      {/* ── 4. Background sections — collapsible accordion ── */}
      {projectGuid && (
        <ProjectContentSections
          projectGuid={projectGuid}
          canEdit={canEdit}
          isEditing={isEditingAbout}
          slot="body"
        />
      )}
    </div>
  );
}
